/**
 * ReportPreviewCard (TV1-2059 · S2)
 *
 * Renders the `generate_report` tool-result carried on an assistant message
 * (`message.reportPreview`) as a chat artifact — the conversational counterpart
 * of the Reports page's inline preview. It REUSES the shipped render + save
 * machinery verbatim (ADR-051), it does not re-implement report generation:
 *
 *  - success -> `toPreviewLayout` turns the unpersisted, positionless body into
 *    the same `PersistedWidgetLayout` the manual builder renders, drawn through
 *    the very same `WidgetLayoutComponent` (read-only). Save is a pure
 *    pass-through to the existing `AddUiWidgetLayout` command — the same command,
 *    id, and `toCommandBody` transform the Reports page uses.
 *  - failure -> the structured 422 the S1 backend tool already classified,
 *    rendered exactly like `GenerateReportBar`'s failure alert (the three
 *    reasons stay distinct — collapsing them is a lie the seller acts on).
 *  - `unanswerable` may carry a NEAREST report; like the Reports page it renders
 *    UNDER the banner (never instead of it) and is saveable.
 *
 * Save dispatches through the in-process client library (`useBackend`), exactly
 * as the Reports page does — it is NOT a CDSE store command, so the aiAssistant
 * store's `canApply` (unconditionally `() => true`) never sees it and needs no
 * allow-list entry.
 *
 * data-testids:
 * - `ai-assistant-report-preview`             — container
 * - `ai-assistant-report-preview-error`       — failure alert (carries data-reason)
 * - `ai-assistant-report-preview-save`        — Save button
 * - `ai-assistant-report-preview-save-error`  — save-failure notice
 */

import { Alert, Button, List, Stack, Text, useComputedColorScheme } from '@mantine/core';
import { IconAlertTriangle, IconDeviceFloppy } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useBackend, { BackendContextType } from '@contexts/useBackend/useBackend';
import WidgetLayoutComponent from '@components/application/WidgetLayout/WidgetLayoutComponent';
import { ALL_WIDGET_TYPES } from '@components/application/WidgetLayout/widgetCatalog';
import { toCommandBody } from '@components/application/WidgetLayout/Widgets/widgetDefinitionAdapter';
import { toPreviewLayout } from '@components/application/Reports/GenerateReport/generateReport';
import { aiAssistantTestIds } from '@domains/aiAssistant/testids';
import type { AIChatMessage } from '../state/state';
import classes from './ReportPreviewCard.module.css';

/**
 * The renderable set — DERIVED from `widgetCatalog`, the same single source the
 * Reports page's `AVAILABLE_WIDGETS` derives from (TV1-2291 · S18/S31).
 *
 * It is deliberately the WHOLE catalog, never a chat-specific subset: the chat
 * preview has to be able to draw anything the generator can emit, so a type this
 * list omitted would render as `undefined` in chat and correctly after saving —
 * the exact "works after save, not in preview" gap the shared renderer exists to
 * prevent. Until S18 both sides were hand-maintained literals whose agreement
 * rested on nothing but a comment saying they were "kept identical"; deriving
 * them means a new widget type reaches both surfaces from one data edit.
 *
 * `ALL_WIDGET_TYPES` is a module-level constant, so the reference is stable across
 * renders — `WidgetLayoutComponent` keys an effect on this prop's identity.
 */
const AVAILABLE_WIDGETS = ALL_WIDGET_TYPES;

export interface ReportPreviewCardProps {
  message: AIChatMessage;
}

export function ReportPreviewCard({ message }: ReportPreviewCardProps ) {
  const artifact = message.reportPreview;
  const backendCtx = useBackend() as BackendContextType | null;
  const { t } = useTranslation( 'interactions' );
  const colorScheme = useComputedColorScheme( 'light' );
  const [ isSaving, setIsSaving ] = useState( false );
  const [ isSaved, setIsSaved ] = useState( false );
  const [ saveFailed, setSaveFailed ] = useState( false );

  // A success body, OR the nearest report an `unanswerable` failure carried.
  // Both are saved through the identical path.
  const previewBody = useMemo(() => {
    if ( !artifact ) return undefined;
    return artifact.status === 'success' ? artifact.body : artifact.failure.nearestReport;
  }, [artifact]);

  // Mint the layout ONCE per artifact so the previewed id and the saved id
  // cannot disagree (and saving twice cannot create two).
  const layout = useMemo(
    () => previewBody ? toPreviewLayout( previewBody ) : undefined,
    [previewBody]
  );

  if ( !artifact ) return null;

  const handleSave = async () => {
    if ( !layout || !backendCtx?.backend ) return;
    setIsSaving( true );
    setSaveFailed( false );
    // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- surfaced to the user via the save-error notice below; not a silent swallow (mirrors Reports.tsx TV1-2010 waiver).
    try {
      await backendCtx.backend.commands.AddUiWidgetLayout({
        id: layout.id,
        body: toCommandBody( layout )
      });
      setIsSaved( true );
    } catch ( err ) {
      console.error( 'AddUiWidgetLayout (chat report) failed', err );
      setSaveFailed( true );
    } finally {
      setIsSaving( false );
    }
  };

  const failure = artifact.status === 'failure' ? artifact.failure : undefined;

  return (
    <div className={classes.reportPreviewCard} data-testid={aiAssistantTestIds.reportPreview}>
      {failure && (
        <Alert
          variant="light"
          color={failure.reason === 'unanswerable' ? 'yellow' : 'red'}
          icon={<IconAlertTriangle size={16} />}
          data-testid={aiAssistantTestIds.reportPreviewError}
          data-reason={failure.reason}
        >
          {failure.reason === 'unanswerable' ? (
            <Stack gap={4}>
              <Text size="sm">
                {failure.explanation
                  ?? t( 'ai_assistant_report_unanswerable', 'Los datos disponibles no permiten responder eso.' )}
              </Text>
              {failure.missingConcepts && failure.missingConcepts.length > 0 && (
                <>
                  <Text size="xs" c="dimmed">
                    {t( 'ai_assistant_report_missing_concepts', 'Falta en el catálogo:' )}
                  </Text>
                  <List size="xs" spacing={2}>
                    {failure.missingConcepts.map(( concept ) => (
                      <List.Item key={concept}>{concept}</List.Item>
                    ))}
                  </List>
                </>
              )}
            </Stack>
          ) : failure.reason === 'validation-exhausted' ? (
            <Text size="sm">
              {t( 'ai_assistant_report_validation_exhausted', 'No pudimos armar ese reporte. Probá reformular la consulta.' )}
            </Text>
          ) : (
            <Text size="sm">
              {t( 'ai_assistant_report_failed', 'No pudimos generar el reporte. Intentá de nuevo.' )}
            </Text>
          )}
        </Alert>
      )}

      {layout && (
        <>
          <div className={classes.previewCanvas}>
            <WidgetLayoutComponent
              layout={layout}
              onChange={() => undefined}
              isEditModeOn={false}
              availableWidgets={AVAILABLE_WIDGETS}
            />
          </div>
          <div className={classes.previewActions}>
            {isSaved ? (
              <Text size="xs" c="dimmed" data-testid={aiAssistantTestIds.reportPreviewSaved}>
                {t( 'ai_assistant_report_saved', 'Reporte guardado.' )}
              </Text>
            ) : (
              <Button
                data-testid={aiAssistantTestIds.reportPreviewSave}
                size="compact-xs"
                variant={colorScheme === 'dark' ? 'filled' : 'light'}
                color="primary"
                leftSection={<IconDeviceFloppy size={14} />}
                onClick={handleSave}
                loading={isSaving}
              >
                {t( 'ai_assistant_report_save', 'Guardar reporte' )}
              </Button>
            )}
            {saveFailed && (
              <Text size="xs" c="red" data-testid={aiAssistantTestIds.reportPreviewSaveError}>
                {t( 'ai_assistant_report_save_failed', 'No pudimos guardar el reporte. Intentá de nuevo.' )}
              </Text>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ReportPreviewCard;
